<div class="document-upload">
	<!-- Hidden file input -->
	<input
		#fileInput
		type="file"
		[accept]="accept()"
		[multiple]="multiple()"
		[disabled]="isUploadDisabled()"
		(change)="onFileSelected($event)"
		style="display: none"
	/>

	<!-- Upload trigger button (shown when no files or in multiple mode) -->
	@if (!hasFiles() || multiple()) {
		<div class="upload-trigger" [class.lvr-error]="hasError() && pendingUploads().length === 0">
			<button
				matButton
				class="trailing-icon-button"
				type="button"
				[disabled]="isUploadDisabled()"
				(click)="onFileButtonClick()"
			>
				@if (showIcon()) {
					<mat-icon aria-hidden="true" [fontIcon]="icon()"></mat-icon>
				}
				@if (multiple()) {
					{{ 'base.field.documentUpload.selectFiles' | transloco }}
				} @else {
					{{ 'base.field.documentUpload.selectFile' | transloco }}
				}
			</button>

			@if (isMaxFileCountReached()) {
				<div class="upload-hint">
					{{ 'base.form.error.maxFileCountReached' | transloco }}
				</div>
			} @else if (selectionMessage() === 'maxFileCountExceeded') {
				<div class="upload-hint">
					{{ 'base.form.error.maxFiles' | transloco: { max: maxFileCount() } }}
				</div>
			} @else if (maxFileSize() && !hasError()) {
				<div class="upload-hint">
					{{ 'base.field.documentUpload.maxFileSize' | transloco: { size: maxFileSize()! / 1024 / 1024 } }}
				</div>
			} @else if (hasError() && pendingUploads().length === 0) {
				<div class="upload-hint lvr-error">
					{{ 'base.form.error.' + hasError() | transloco: { size: maxFileSize()! / 1024 / 1024, max: maxFileCount() } }}
				</div>
			}
		</div>
	}

	<!-- Upload progress and file list -->
	@if (uploads().length > 0 || currentValue().length > 0) {
		<div class="upload-list">
			<!-- Uploading list -->
			@for (upload of pendingUploads(); track $index) {
				<div
					class="upload-item"
					[class.upload-error]="upload.status() === 'error'"
					[class.lvr-error]="hasError() !== '' || upload.status() === 'error'"
				>
					<!-- File info -->
					<div class="file-info">
						<div class="file-name">
							<mat-icon class="file-icon">
								@switch (upload.file!.type.split('/')[0]) {
									@case ('image') {
										image
									}
									@case ('application') {
										@if (upload.file!.type.includes('pdf')) {
											picture_as_pdf
										} @else {
											description
										}
									}
									@default {
										insert_drive_file
									}
								}
							</mat-icon>
							<span>{{ upload.file!.name }}</span>
						</div>

						<div class="file-size">
							{{ formatFileSize(upload.file!.size) }}
						</div>
					</div>

					<!-- Upload progress -->
					@switch (upload.status()) {
						@case ('uploading') {
							<div class="upload-progress">
								<mat-progress-bar mode="determinate" [value]="upload.progress()"></mat-progress-bar>
								<span class="progress-text">{{ upload.progress() }}%</span>
							</div>
						}
						@case ('completed') {
							<div class="upload-completed">
								<mat-icon color="primary">check_circle</mat-icon>
								<span>{{ 'base.field.documentUpload.uploadComplete' | transloco }}</span>
							</div>
						}
						@case ('error') {
							<div class="upload-error-message">
								<mat-icon color="warn">error</mat-icon>
								<span>{{ upload.error || ('base.field.documentUpload.uploadFailed' | transloco) }}</span>
							</div>
						}
						@case ('cancelled') {
							<div class="upload-cancelled">
								<mat-icon>cancel</mat-icon>
								<span>{{ 'base.field.documentUpload.uploadCancelled' | transloco }}</span>
							</div>
						}
						@default {
							<div class="upload-pending">
								<mat-icon>hourglass_empty</mat-icon>
								<span>{{ 'base.field.documentUpload.preparing' | transloco }}</span>
							</div>
						}
					}

					<!-- Action buttons -->
					<div class="upload-actions">
						@switch (upload.status()) {
							@case ('uploading') {
								<button
									mat-icon-button
									type="button"
									[disabled]="disabled()"
									(click)="cancelUpload($index)"
									[attr.aria-label]="'base.field.documentUpload.cancelUpload' | transloco"
								>
									<mat-icon>cancel</mat-icon>
								</button>
							}
							@case ('error') {
								<button
									mat-icon-button
									type="button"
									[disabled]="disabled()"
									(click)="removeCompletedFile($index)"
									[attr.aria-label]="'base.field.documentUpload.removeFile' | transloco"
								>
									<mat-icon>delete</mat-icon>
								</button>
							}
							@case ('cancelled') {
								<button
									mat-icon-button
									type="button"
									[disabled]="disabled()"
									(click)="removeCompletedFile($index)"
									[attr.aria-label]="'base.field.documentUpload.removeFile' | transloco"
								>
									<mat-icon>delete</mat-icon>
								</button>
							}
						}
					</div>
				</div>
			}
			<!-- Uploaded list -->
			@for (val of currentValue(); track $index) {
				<div class="upload-item">
					<!-- File info -->
					<div class="file-info">
						<div class="file-name">
							<mat-icon class="file-icon">
								@switch (val.mimeType.split('/')[0]) {
									@case ('image') {
										image
									}
									@case ('application') {
										@if (val.mimeType.includes('pdf')) {
											picture_as_pdf
										} @else {
											description
										}
									}
									@default {
										insert_drive_file
									}
								}
							</mat-icon>
							<span>{{ val.name }}</span>
						</div>

						<div class="file-size">
							{{ formatFileSize(val.size) }}
						</div>
					</div>

					<!-- Upload progress -->
					<div class="upload-completed">
						<mat-icon color="primary">check_circle</mat-icon>
						<span>{{ 'base.field.documentUpload.uploadComplete' | transloco }}</span>
					</div>
					<!-- Action buttons -->
					<div class="upload-actions">
						@if (!val?.isFrozen) {
							<button
								mat-icon-button
								type="button"
								[disabled]="disabled()"
								(click)="removeCompletedFile($index)"
								[attr.aria-label]="'base.field.documentUpload.removeFile' | transloco"
							>
								<mat-icon>delete</mat-icon>
							</button>
						}
					</div>
				</div>
			}
		</div>
	}
</div>
